import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import client from "../../../api";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { getErrorMessage } from "src/lib/utils";
import {
  Table,
  Modal,
  Tag,
  Button,
  Tabs,
  Card,
  Typography,
  Form,
  Input,
  Switch,
  Row,
  Col,
  Popconfirm,
} from "antd";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { EyeIcon } from "src/components/icons/eye-icon";

const { TextArea } = Input;
const { Title, Text } = Typography;

const FaqManagement = () => {
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.LANDING_PAGE_MANAGEMENT_FAQ
  );
  const canViewFaqs = canView || canList;

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tableData, setTableData] = useState<[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: LIST_PAGE_SIZE,
    total: 0,
  });
  const [selectedFaq, setSelectedFaq] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFaqId, setEditingFaqId] = useState<number | null>(null);

  const { mutate: getAllFaqsList, isLoading } = useMutation(
    client.landingPageApis.GetAllFaqsList,
    {
      onSuccess: (res: any) => {
        const formatted = (res?.items || []).map((item: any) => ({
          ...item,
          questionPreview: item.question?.[2] || "-", // English
        }));

        setTableData(formatted);

        setPagination((prev) => ({
          ...prev,
          total: res?.pagination?.total || 0,
        }));
      },

      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }

        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: createFaq, isLoading: isLoadingCreateFaq } = useMutation(
    client.landingPageApis.CreateNewFaq,
    {
      onSuccess: () => {
        form.resetFields();

        setShowCreateModal(false);

        getAllFaqsList({
          page: pagination.page,
          limit: pagination.limit,
        });
      },

      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }

        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: updateFaq, isLoading: isLoadingUpdateFaq } =
    useMutation(
      client.landingPageApis.UpdateFaq,
      {
        onSuccess: () => {
          form.resetFields();

          setShowCreateModal(false);

          setIsEditMode(false);

          setEditingFaqId(null);

          getAllFaqsList({
            page: pagination.page,
            limit: pagination.limit,
          });
        },

        onError: (error: any) => {
          if (error?.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else if (error?.response) {
            setAlertText(
              getErrorMessage(
                error.response.data,
              ),
            );
          } else {
            setAlertText(
              APISomethingWrongMsg,
            );
          }

          setIsAlertOpened(true);
        },
      },
    );
  const handleCreateFaq = (
    values: any,
  ) => {
    const payload = {
      question: [
        values.question_ge,
        values.question_ru,
        values.question_en,
      ],

      answer: [
        values.answer_ge,
        values.answer_ru,
        values.answer_en,
      ],

      active:
        values.active ?? true,
    };

    if (isEditMode) {
      if (!canEdit) {
        setAlertText("You do not have permission to edit FAQs");
        setIsAlertOpened(true);
        return;
      }
      updateFaq({
        id: editingFaqId,
        ...payload,
      });

      return;
    }

    if (!canAdd) {
      setAlertText("You do not have permission to add FAQs");
      setIsAlertOpened(true);
      return;
    }
    createFaq(payload);
  };

  const handleEditFaq = (
    faq: any,
  ) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit FAQs");
      setIsAlertOpened(true);
      return;
    }
    setIsEditMode(true);

    setEditingFaqId(faq.id);

    form.setFieldsValue({
      question_ge:
        faq.question?.[0],

      question_ru:
        faq.question?.[1],

      question_en:
        faq.question?.[2],

      answer_ge:
        faq.answer?.[0],

      answer_ru:
        faq.answer?.[1],

      answer_en:
        faq.answer?.[2],

      active: faq.active,
    });

    setShowCreateModal(true);
  };

  const { mutate: deleteFaq } = useMutation(
    client.landingPageApis.DeleteFaq,
    {
      onSuccess: () => {
        getAllFaqsList({
          page: pagination.page,
          limit: pagination.limit,
        });
      },
    },
  );

  const onDelete = (id: number) => {
    if (!canDelete) {
      setAlertText("You do not have permission to delete FAQs");
      setIsAlertOpened(true);
      return;
    }
    deleteFaq(id);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPagination((prev) => ({
        ...prev,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (!isMounted || !canViewFaqs) return;

    const payload: any = {
      page: pagination.page,
      limit: pagination.limit,
    };
    getAllFaqsList(payload);
  }, [isMounted, canViewFaqs, pagination.page, debouncedSearch]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },

    {
      title: "Question",
      render: (_: any, record: any) =>
        record.question?.[2] || record.question?.[0],
    },

    {
      title: "Status",
      render: (_: any, record: any) => (
        <Tag color={record.active ? "green" : "red"}>
          {record.active ? "Active" : "Inactive"}
        </Tag>
      ),
    },

    {
      title: "Updated",
      render: (_: any, record: any) =>
        new Date(record.updatedAt).toLocaleDateString(),
    },

    {
      title: "Action",
      width: 220,

      render: (_: any, record: any) => {
        if (!canView && !canEdit && !canDelete) {
          return "—";
        }
        return (
          <div className="flex items-center gap-3">
            {canView && (
              <button
                className="
        flex items-center justify-center
        rounded-lg
        p-2
        border
        border-gray-200
        bg-white
        hover:bg-gray-50
      "
                onClick={() => {
                  setSelectedFaq(record);
                  setShowModal(true);
                }}
              >
                <EyeIcon
                  className="w-5 h-5"
                  fill="#6b7280"
                  stroke="#6b7280"
                />
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="
              flex items-center justify-center
              rounded-lg
              p-2
              border
              border-gray-200
              bg-white
              hover:bg-blue-50
            "
                onClick={() => handleEditFaq(record)}
              >
                <EditIcon
                  className="w-5 h-5"
                  fill="#2563eb"
                  stroke="#2563eb"
                />
              </button>
            )}

            {canDelete && (
              <Popconfirm
                title="Delete FAQ"
                description="Are you sure you want to delete this FAQ?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{
                  style: {
                    backgroundColor: "#ef4444",
                    borderColor: "#ef4444",
                    color: "#fff",
                  },
                }}
              >
                <button
                  type="button"
                  className="
                flex items-center justify-center
                rounded-lg
                p-2
                border
                border-red-200
                bg-white
                hover:bg-red-50
              "
                >
                  <DeleteIcon
                    className="w-5 h-5"
                    fill="#ef4444"
                    stroke="#ef4444"
                  />
                </button>
              </Popconfirm>
            )}
          </div>
        );
      },
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-2xl font-bold mb-1">
          FAQs
        </h2>
        <PermissionGate
          module={[permissionConstants.LANDING_PAGE_MANAGEMENT_FAQ]}
          action="can_add"
        >
          <Button
            type="primary"
            size="large"
            onClick={() => {
              setIsEditMode(false);

              setEditingFaqId(null);

              form.resetFields();

              form.setFieldsValue({
                active: true,
              });

              setShowCreateModal(true);
            }} style={{
              background: "#1677ff",
              borderColor: "#1677ff",
            }}
          >
            + Add FAQ
          </Button>
        </PermissionGate>
      </div>

      {!canViewFaqs ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-xl p-8 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view FAQs.
          </p>
        </div>
      ) : (
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={tableData}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,

            onChange: (page) => {
              setPagination((prev) => ({
                ...prev,
                page,
              }));
            },
          }}
        />
      )}

      {/* VIEW MODAL */}

      <Modal
        open={showModal}
        width={800}
        footer={null}
        title="FAQ Details"
        onCancel={() => {
          setShowModal(false);
        }}
      >
        {selectedFaq && (
          <Tabs
            items={[
              {
                key: "1",

                label: "🇬🇪 Georgian",

                children: (
                  <Card>
                    <Title level={5}>Question</Title>

                    <Text>{selectedFaq.question?.[0]}</Text>

                    <br />
                    <br />

                    <Title level={5}>Answer</Title>

                    <Text>{selectedFaq.answer?.[0]}</Text>
                  </Card>
                ),
              },

              {
                key: "2",

                label: "🇷🇺 Russian",

                children: (
                  <Card>
                    <Title level={5}>Question</Title>

                    <Text>{selectedFaq.question?.[1]}</Text>

                    <br />
                    <br />

                    <Title level={5}>Answer</Title>

                    <Text>{selectedFaq.answer?.[1]}</Text>
                  </Card>
                ),
              },

              {
                key: "3",

                label: "🇬🇧 English",

                children: (
                  <Card>
                    <Title level={5}>Question</Title>

                    <Text>{selectedFaq.question?.[2]}</Text>

                    <br />
                    <br />

                    <Title level={5}>Answer</Title>

                    <Text>{selectedFaq.answer?.[2]}</Text>
                  </Card>
                ),
              },
            ]}
          />
        )}
      </Modal>

      {/* CREATE MODAL */}

      <Modal
        open={showCreateModal}
        width={900}
        title={
          isEditMode
            ? "Edit FAQ"
            : "Add FAQ"
        }
        okText={
          isEditMode
            ? "Update FAQ"
            : "Create FAQ"
        }
        confirmLoading={
          isEditMode
            ? isLoadingUpdateFaq
            : isLoadingCreateFaq
        } onCancel={() => {
          setShowCreateModal(false);
          setIsEditMode(false);
          setEditingFaqId(null);
          form.resetFields();
        }}
        okButtonProps={{
          style: {
            backgroundColor: "#2563eb",
            borderColor: "#2563eb",
            color: "#ffffff",
          },
        }}

        cancelButtonProps={{
          style: {
            borderColor: "#d1d5db",
            color: "#374151",
          },
        }}

        onOk={() => {
          form.submit();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            active: true,
          }}
          onFinish={handleCreateFaq}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="🇬🇪 Question (Georgian)"
                name="question_ge"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="🇷🇺 Question (Russian)"
                name="question_ru"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="🇬🇧 Question (English)"
            name="question_en"
            rules={[
              {
                required: true,
                message: "Required",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="🇬🇪 Answer (Georgian)"
                name="answer_ge"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <TextArea rows={4} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="🇷🇺 Answer (Russian)"
                name="answer_ru"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="🇬🇧 Answer (English)"
            name="answer_en"
            rules={[
              {
                required: true,
                message: "Required",
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>

          {/* <Form.Item
            label="Status"
            name="active"
            valuePropName="checked"
          >

            <div className="flex items-center gap-3">
              <Switch
                checkedChildren="ON"
                unCheckedChildren="OFF"
                style={{
                  backgroundColor: form.getFieldValue(
                    "active",
                  )
                    ? "#16a34a"
                    : "#9ca3af",
                }}
              />
            </div>
          </Form.Item> */}
          <Form.Item
            shouldUpdate
            noStyle
          >
            {() => (
              <Switch
                checkedChildren="ON"
                unCheckedChildren="OFF"

                checked={
                  form.getFieldValue(
                    "active",
                  )
                }

                onChange={(value) => {
                  form.setFieldValue(
                    "active",
                    value,
                  );
                }}

                style={{
                  backgroundColor:
                    form.getFieldValue(
                      "active",
                    )
                      ? "#16a34a"
                      : "#9ca3af",
                }}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FaqManagement;
